import { Button, Flex, Heading, Notice, Progress } from '@eleveo/components-react';
import { BinIcon } from '@eleveo/icons-react';
import { TFunction } from 'i18next';
import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { RoleMappingSelect } from './RoleMappingSelect';
import { useIntegrations } from '../../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../../constants/dataTestIds';
import { USER_SYNCHRONIZATION_FIELDS } from '../../../../scene/integrations/types';
import { createRoleOption } from '../../../../utils/roleUtils';
import { Center } from '../../../Center';
import { ETL_INTEGRATION_TYPE } from '../../../configuration/types';
import { useIntegrationRoles } from '../../roles/IntegrationRolesProvider';
import { RoleOption } from '../../roles/types';

const RoleMappingWrapper: React.FC<React.PropsWithChildren<{ t: TFunction }>> = ({ t, children }) => (
  <Flex column gap={20} className="flex-1 p-20 w-full h-full">
    <Heading as="h3" className="text-2xl text-blue-700 font-bold" testId={TEST_IDS.headerRoleMapping}>
      {t('myConfigurations.roleMapping.title')}
    </Heading>
    {children}
  </Flex>
);

type RoleOptionsType = Record<string, RoleOption[] | { name: string; children: RoleOption[] }[]>;

export const RoleMapping: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { isInReadOnlyMode, integrationType } = useIntegrations();
  const { change } = useForm();

  const {
    isEleveoRolesError,
    isIntegrationRolesError,
    isEleveoRolesLoading,
    isIntegrationRolesLoading,
    roleMapping,
    eleveoRoles,
    isIntegrationDataFilled,
    integrationRoles,
    conflictedRoles,
    conflictedOptions,
    isPermittedForIntegrationRoles,
    isPermittedForEleveoRoles,
  } = useIntegrationRoles();

  const handleDeleteDisabledRole = useCallback(
    (disabledRoleId: string) => {
      const newRoleMapping = { ...roleMapping };
      delete newRoleMapping[disabledRoleId];
      change(USER_SYNCHRONIZATION_FIELDS.roleMapping, newRoleMapping);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleMapping]
  );

  const optionsToRoleId: RoleOptionsType = useMemo(
    () => {
      if (!eleveoRoles) {
        return {};
      }

      const result: RoleOptionsType = {};

      const conflictedRoleOptions = Object.keys(conflictedRoles).map(createRoleOption);

      [...eleveoRoles, ...conflictedRoleOptions].forEach((role) => {
        if (!integrationRoles) {
          result[role.id] = [];
          return;
        }

        if (!conflictedOptions.length) {
          result[role.id] = integrationRoles;
          return;
        }

        // show only roles that were previously assigned and removed
        const removedOptionsForRole = conflictedOptions.filter((conflictedOption) => {
          if (!roleMapping[role.id]) {
            return false;
          }
          return roleMapping[role.id].includes(conflictedOption.id);
        });

        if (!removedOptionsForRole.length) {
          result[role.id] = integrationRoles;
          return;
        }

        result[role.id] = [
          { name: t('myConfigurations.roleMapping.roles.deleted'), children: removedOptionsForRole },
          {
            name: t('myConfigurations.roleMapping.roles.providerRoles', {
              providerName: t(`providers.${integrationType}`),
            }),
            children: integrationRoles,
          },
        ];
      });

      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conflictedRoles, conflictedOptions, integrationRoles, eleveoRoles, integrationType, roleMapping]
  );

  const fieldName = useMemo(() => {
    switch (integrationType) {
      case ETL_INTEGRATION_TYPE.wxcc:
      case ETL_INTEGRATION_TYPE.wxcalling:
        return 'organizationId';
      case ETL_INTEGRATION_TYPE.zoomcc:
        return 'accountId';
      case ETL_INTEGRATION_TYPE.msteams:
        return 'tenant';
      default:
        throw new Error('Invalid integration type!');
    }
  }, [integrationType]);

  const hasData = !!(eleveoRoles?.length && integrationRoles?.length);
  const isLoading = isEleveoRolesLoading || isIntegrationRolesLoading;
  const isError = isEleveoRolesError || isIntegrationRolesError;
  const isPermitted = isPermittedForIntegrationRoles && isPermittedForEleveoRoles;

  if (isLoading) {
    return (
      <Center>
        <Progress />
      </Center>
    );
  }

  if (!isPermitted) {
    return (
      <RoleMappingWrapper t={t}>
        {!isPermittedForEleveoRoles && (
          <Notice color="error" testId={TEST_IDS.noticeRoleMappingMissingEleveoPermission}>
            {t('myConfigurations.roleMapping.permissions.missing.forEleveoRoles')}
          </Notice>
        )}
        {!isPermittedForIntegrationRoles && (
          <Notice color="error" testId={TEST_IDS.noticeRoleMappingMissingIntegrationPermission}>
            {t('myConfigurations.roleMapping.permissions.missing.forIntegrationRoles', {
              providerName: t(`providers.${integrationType}`),
            })}
          </Notice>
        )}
      </RoleMappingWrapper>
    );
  }

  if (isError) {
    return (
      <RoleMappingWrapper t={t}>
        {isEleveoRolesError && (
          <Notice color="error" testId={TEST_IDS.noticeRoleMappingEleveoRolesFailed}>
            {t('myConfigurations.roleMapping.error.eleveoRoles')}
          </Notice>
        )}
        {isIntegrationRolesError && (
          <Notice color="error" testId={TEST_IDS.noticeRoleMappingIntegrationRolesFailed}>
            {t('myConfigurations.roleMapping.error.integrationRoles', {
              providerName: t(`providers.${integrationType}`),
            })}
          </Notice>
        )}
      </RoleMappingWrapper>
    );
  }

  return (
    <>
      <RoleMappingWrapper t={t}>
        {children}

        {!isIntegrationDataFilled && (
          <Notice color="info" testId="integrationRole">
            {t('myConfigurations.roleMapping.info', {
              fieldName: t(`myConfigurations.configuration.${fieldName}`),
            })}
          </Notice>
        )}

        {isIntegrationDataFilled && !eleveoRoles?.length && (
          <Notice color="info" testId={TEST_IDS.noticeRoleMappingMissingEleveoRoles}>
            {t('myConfigurations.roleMapping.roles.missingEleveoRoles')}
          </Notice>
        )}

        {isIntegrationDataFilled && !integrationRoles?.length && (
          <Notice color="info" testId={TEST_IDS.noticeRoleMappingMissingIntegrationRoles}>
            {t('myConfigurations.roleMapping.roles.missingIntegrationRoles', {
              providerName: t(`providers.${integrationType}`),
            })}
          </Notice>
        )}

        {isIntegrationDataFilled &&
          hasData &&
          eleveoRoles.map((eleveoRole, index) => (
            <RoleMappingSelect
              className="w-full"
              field={USER_SYNCHRONIZATION_FIELDS.roleMappingSelect(eleveoRole.id)}
              key={eleveoRole.id}
              role={eleveoRole}
              isDisabled={isInReadOnlyMode}
              defaultSelectedKeys={roleMapping[eleveoRole.name]}
              options={optionsToRoleId[eleveoRole.id]}
              validationState={roleMapping[eleveoRole.name] ? 'valid' : undefined}
              testId={TEST_IDS.selectEleveoRole(index)}
            />
          ))}

        {isIntegrationDataFilled && hasData && !!Object.keys(conflictedRoles ?? []).length && (
          <Flex column gap={20} className="flex-1 20">
            <Heading as="h3" className="text-2xl text-blue-700 font-bold" testId={TEST_IDS.headerDeactivatedRoleMapping}>
              {t('myConfigurations.deactivatedRoleMapping')}
            </Heading>
            <Notice color="error" testId={TEST_IDS.noticeDeactivatedRoleMapping}>
              {t('myConfigurations.deactivatedRoleMappingError')}
            </Notice>
            {integrationRoles &&
              Object.keys(conflictedRoles ?? {}).map((disabledRoleId, index) => (
                <Flex gap={10} className="w-full" key={`${disabledRoleId}-disabled`}>
                  <RoleMappingSelect
                    className="w-full"
                    field={USER_SYNCHRONIZATION_FIELDS.roleMappingSelect(disabledRoleId)}
                    isDisabled={isInReadOnlyMode}
                    role={{ name: disabledRoleId, id: disabledRoleId }}
                    defaultSelectedKeys={conflictedRoles?.[disabledRoleId]}
                    options={optionsToRoleId[disabledRoleId]}
                    disabledKeys={[...integrationRoles.map((r) => r.id), ...conflictedOptions.map((option) => option.id)]}
                    validationState="invalid"
                    testId={TEST_IDS.deactivatedEleveoRole(index)}
                  />
                  <Button
                    variant="quiet"
                    color="error"
                    testId={TEST_IDS.buttonDeleteDeactivatedEleveoRole(index)}
                    aria-label={t('myConfigurations.deleteDeactivatedRoleMapping', { roleName: disabledRoleId })}
                    onClick={() => handleDeleteDisabledRole(disabledRoleId)}
                  >
                    <BinIcon />
                  </Button>
                </Flex>
              ))}
          </Flex>
        )}
      </RoleMappingWrapper>
    </>
  );
};
